@if (isMobile()) {
    <!-- On mobile the picker opens in a dialog instead of a dropdown menu. -->
    <button
        type="button"
        class="period-select-trigger"
        [disabled]="isDisabled"
        (click)="openMobileDialog(mobileDialog)">
        <ng-container *ngTemplateOutlet="triggerLabel"/>
    </button>
} @else {
    <button
        type="button"
        class="period-select-trigger"
        [disabled]="isDisabled"
        [matMenuTriggerFor]="periodMenu">
        <ng-container *ngTemplateOutlet="triggerLabel"/>
    </button>
}

<ng-template #triggerLabel>
    <span class="period-select-label">{{ "asset_details.period" | transloco }}</span>
    <span class="period-select-value">{{ selectedLabel() ?? (interval()?.translationKey | transloco) }}</span>
    <mat-icon class="period-select-arrow">arrow_drop_down</mat-icon>
</ng-template>

<!-- Desktop: dropdown menu -->
<mat-menu #periodMenu="matMenu" xPosition="before" class="period-menu-panel">
    <ng-container *ngTemplateOutlet="pickerBody"/>
</mat-menu>

<!-- Mobile: same body rendered inside a dialog -->
<ng-template #mobileDialog>
    <ng-container *ngTemplateOutlet="pickerBody"/>
</ng-template>

<ng-template #pickerBody>
    <div class="period-menu" [class.mobile]="isMobile()">
        <div class="period-options">
            @for (p of predefinedIntervals; track p) {
                <button
                    type="button"
                    class="period-option"
                    [class.active]="interval() === p"
                    (click)="selectInterval(p)">
                    {{ p.translationKey | transloco }}
                </button>
            }
        </div>

        <div class="period-divider"></div>

        @if (isMobile()) {
            <!-- Single calendar on phones; both month arrows live on it. -->
            <ng-container
                *ngTemplateOutlet="calendar; context: { month: viewMonth(), grid: leftGrid(), side: 'single' }"/>
        } @else {
            <ng-container
                *ngTemplateOutlet="calendar; context: { month: viewMonth(), grid: leftGrid(), side: 'left' }"/>

            <div class="period-divider"></div>

            <ng-container
                *ngTemplateOutlet="calendar; context: { month: rightMonth(), grid: rightGrid(), side: 'right' }"/>
        }
    </div>
</ng-template>

<!-- Time selector row. Start time applies to the `from` date, end to the `to`
     date; the picked date is shown beside the input so the hour reads clearly. -->
<ng-template #timeRow let-which="which">
    @if (which === 'start') {
        <mat-form-field class="period-time" appearance="outline">
            <mat-label>
                {{ "period.start_time" | transloco }}
                @if (calendarRange()?.[0]; as from) {
                    • {{ formatDay(from) }}
                }
            </mat-label>
            <input matInput type="time" [value]="startTime()" [disabled]="!calendarRange()?.[1]"
                   (change)="setStartTime($any($event.target).value)">
        </mat-form-field>
    } @else {
        <mat-form-field class="period-time" appearance="outline">
            <mat-label>
                {{ "period.end_time" | transloco }}
                @if (calendarRange()?.[1]; as to) {
                    • {{ formatDay(to) }}
                }
            </mat-label>
            <input matInput type="time" [value]="endTime()" [disabled]="!calendarRange()?.[1]"
                   (change)="setEndTime($any($event.target).value)">
        </mat-form-field>
    }
</ng-template>

<ng-template #calendar let-month="month" let-grid="grid" let-side="side">
    <!-- Non-interactive container: the click handler only keeps interior clicks from closing the menu/dialog. -->
    <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
    <div class="calendar" (click)="$event.stopPropagation()">
        <div class="header">
            @if (side === 'left' || side === 'single') {
                <button type="button" class="nav" aria-label="Previous month" (click)="previousMonth()">
                    <mat-icon>chevron_left</mat-icon>
                </button>
            } @else {
                <span class="nav-spacer"></span>
            }

            <span class="month-label">{{ monthLabel(month) }}</span>

            @if (side === 'right' || side === 'single') {
                <button type="button" class="nav" aria-label="Next month" (click)="nextMonth()">
                    <mat-icon>chevron_right</mat-icon>
                </button>
            } @else {
                <span class="nav-spacer"></span>
            }
        </div>

        <div class="grid" (mouseleave)="hovered.set(null)">
            @for (weekday of weekdays; track weekday) {
                <div class="weekday">{{ weekday.toString().charAt(0) }}</div>
            }
            <div class="weekday-divider"></div>
            @for (item of grid; track item) {
                @let inMonth = isCurrentMonth(item, month);
                <div
                    class="cell"
                    [class.range-start]="inMonth && isRangeStart(item)"
                    [class.range-end]="inMonth && isRangeEnd(item)"
                    [class.in-range]="inMonth && isInRange(item)">
                    <button
                        type="button"
                        class="day"
                        [class.selected]="inMonth && (isSelectedEndpoint(item) || isPreviewEndpoint(item))"
                        [disabled]="!inMonth"
                        (mouseenter)="hovered.set(item)"
                        (click)="handleDaySelected(item)">
                        {{ item.dayOfMonth() }}
                    </button>
                </div>
            }
        </div>

        <div class="period-times">
            @if (side === 'right') {
                <ng-container *ngTemplateOutlet="timeRow; context: { which: 'end' }"/>
            } @else if (side === 'left') {
                <ng-container *ngTemplateOutlet="timeRow; context: { which: 'start' }"/>
            } @else {
                <ng-container *ngTemplateOutlet="timeRow; context: { which: 'start' }"/>
                <ng-container *ngTemplateOutlet="timeRow; context: { which: 'end' }"/>
            }
        </div>
    </div>
</ng-template>